import CustomHtmlKeyboardResponsePlugin from "../../../customPlugin/customHtmlKeyboardResponse";
import { resolveLayoutPlaceholders } from "../../../lib/utils";

/**
 * Creates a practice choice trial that asks the user if they want to review/practice
 * what they learned in a previous mission
 * 
 * @param {JsPsych} jsPsych - The jsPsych instance
 * @param {string} previousMission - The mission number/name to reference (e.g., "Mission 0", "the previous mission")
 * @returns {Object} A jsPsych trial object for the practice choice screen
 */
export function buildPracticeChoiceTrial(jsPsych, previousMission = null) {
  const practiceLabel = previousMission
    ? `what you learned in ${previousMission}`
    : "the basic controls";

  const stimulusTemplate = `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: Arial, sans-serif; background: #3DA7C2;">
        <div style="background: white; padding: 50px 40px; border-radius: 20px; max-width: 650px; text-align: center; box-shadow: 0 20px 60px rgba(0,0,0,0.4); border-top: 6px solid #2c3e50;">
          <div style="margin-bottom: 30px;">
            <img src="assets/images/driver/happyDriver.svg" alt="Happy Driver" style="width: 120px; height: 120px; filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));">
          </div>
          <h1 style="color: #2c3e50; font-size: 32px; margin-bottom: 20px; font-weight: bold;">Practice Session</h1>
          <p style="color: #555; font-size: 20px; margin-bottom: 40px; line-height: 1.6;">
            Would you like to review/practice ${practiceLabel}?
          </p>
          <div style="display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
            <div style="background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%); padding: 20px 40px; border-radius: 12px; box-shadow: 0 4px 15px rgba(127, 140, 141, 0.3); transition: transform 0.2s; cursor: pointer;">
              <p style="color: white; font-size: 22px; margin: 0; font-weight: bold;">Press <span style="background: rgba(255,255,255,0.3); padding: 4px 12px; border-radius: 6px; display: inline-block; margin: 0 4px;">__BACKWARD_KEY__</span> for No</p>
            </div>
            <div style="background: linear-gradient(135deg, #3DA7C2 0%, #2a8ca0 100%); padding: 20px 40px; border-radius: 12px; box-shadow: 0 4px 15px rgba(61, 167, 194, 0.3); transition: transform 0.2s; cursor: pointer;">
              <p style="color: white; font-size: 22px; margin: 0; font-weight: bold;">Press <span style="background: rgba(255,255,255,0.3); padding: 4px 12px; border-radius: 6px; display: inline-block; margin: 0 4px;">__FORWARD_KEY__</span> for Yes</p>
            </div>
          </div>
        </div>
      </div>
    `;

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus: stimulusTemplate,
    on_start: (trial) => {
      jsPsych?.customKeyboardResponse?.clearHeldKeys?.();
      trial.stimulus = resolveLayoutPlaceholders(jsPsych, stimulusTemplate);
    },
    choices: ["KeyZ", "Slash"],
    on_finish: (data) => {
      const responseCode = data.response_event_code ?? data.response;
      const responseLabel = data.response;
      data.userWantsPractice = responseCode === "Slash" || responseLabel === "/";
      data.name = "practiceChoice";
    },
  };
}

/**
 * Creates a conditional function that checks if the user wants to practice
 * based on their response to the practice choice trial
 * 
 * @param {JsPsych} jsPsych - The jsPsych instance
 * @param {boolean} askRepractice - Whether to ask for repractice (if false, always returns true)
 * @returns {Function} A function that returns true if practice should be shown
 */
export function createPracticeChoiceChecker(jsPsych, askRepractice = true) {
  return () => {
    if (!askRepractice) return true;
    const data = jsPsych.data.get().filter({ name: "practiceChoice" }).last(1).values();
    return data.length > 0 && data[0].userWantsPractice;
  };
}
